export interface ValidationErrors {
  guestName?: string;
  phone?: string;
  guestCount?: string;
  adultCount?: string;
  childCount?: string;
  weddingCode?: string;
  singleAttendanceWarning?: string;
}

interface RSVPFormData {
  guestName?: string;
  phone?: string;
  attending: boolean;
  adultCount?: number;
  childCount?: number;
  weddingCode?: string;
  confirmSingleAttendance?: boolean;
}

// Valid wedding codes - updated list
const VALID_WEDDING_CODES = [
  'domkym25', 'arsanau25', 'petcec25', 'yvonlex25', 'josiah25', 'gabriel25',
  'sham25', 'frank25', 'chaifel25', 'ronbeck25', 'husagn25', 'rickrys25',
  'suhudaw25', 'casspri25', 'andwin25', 'chagat25', 'ramos25', 'vivi25',
  'jane25', 'banmicmay25', 'roslyn25', 'chelaar25', 'benj25', 'thiasel25',
  'limjan25', 'ryumiz25', 'marctab25', 'izurei25', 'jonstep25', 'denhae25',
  'angmuj25', 'desman25', 'anndavdav25', 'dickhel25', 'yumryo25', 'kongjoa25',
  'shinmik25', 'kohtam25', 'hyoin25', 'sheena25', 'stevlis25', 'mikdeb25',
  'chinang25', 'johsue25', 'thanya25', 'dama25', 'lazarus25', 'shanlim25',
  'simnancy25', 'matlis25', 'shuena25', 'bardar25', 'patrick25', 'randy25',
  'nancy25', 'yin25', 'hos25', 'anddol25', 'andmun25', 'kijseon25', 'koiyur25',
  'nanami25', 'kana25', 'inikung25', 'hapyii25', 'yianmei25', 'edwyn25',
  // NEW FILLER GROUPS
  'kadik25', 'pendi25', 'yosua25', 'elah25', 'chiharu25', 'gawai25',
  // TEST_CODES
  'dominickemberly2025'
];

// Guest mapping for validation (mirrored from API)
const GUEST_WEDDING_CODE_MAPPING: Record<string, {
  expectedNames: string[];
  description: string;
}> = {
  // All Wedding Guests
  'domkym25': { expectedNames: ['Dom & Kym', 'Dom, Kym', 'Dom Kym', 'Kym & Dom', 'Kym, Dom', 'Kym Dom'], description: 'Dom & Kym' },
  'arsanau25': { expectedNames: ['Arsat & Anau', 'Arsat, Anau', 'Arsat Anau', 'Anau & Arsat', 'Anau, Arsat', 'Anau Arsat'], description: 'Arsat & Anau' },
  'petcec25': { expectedNames: ['Peter & Cecilia', 'Peter, Cecilia', 'Peter Cecilia', 'Cecilia & Peter', 'Cecilia, Peter', 'Cecilia Peter'], description: 'Peter & Cecilia' },
  'yvonlex25': { expectedNames: ['Yvonne, Ian, Lexi', 'Yvonne & Ian & Lexi', 'Yvonne Ian Lexi'], description: 'Yvonne, Ian, Lexi' },
  'josiah25': { expectedNames: ['Josiah'], description: 'Josiah' },
  'gabriel25': { expectedNames: ['Gabriel'], description: 'Gabriel' },
  'sham25': { expectedNames: ['Shammuel'], description: 'Shammuel' },
  'frank25': { expectedNames: ['Frank'], description: 'Frank' },
  'chaifel25': { expectedNames: ['Chai & Felicia', 'Chai, Felicia', 'Chai Felicia', 'Felicia & Chai', 'Felicia, Chai', 'Felicia Chai'], description: 'Chai & Felicia' },
  'ronbeck25': { expectedNames: ['Ron & Becky', 'Ron, Becky', 'Ron Becky', 'Becky & Ron', 'Becky, Ron', 'Becky Ron'], description: 'Ron & Becky' },
  'husagn25': { expectedNames: ['Hussein & Agnes', 'Hussein, Agnes', 'Hussein Agnes', 'Agnes & Hussein', 'Agnes, Hussein', 'Agnes Hussein'], description: 'Hussein & Agnes' },
  'rickrys25': { expectedNames: ['Riccie & Krystel', 'Riccie, Krystel', 'Riccie Krystel', 'Krystel & Riccie', 'Krystel, Riccie', 'Krystel Riccie'], description: 'Riccie & Krystel' },
  'suhudaw25': { expectedNames: ['Su Hua & Dawn', 'Su Hua, Dawn', 'Su Hua Dawn', 'Dawn & Su Hua', 'Dawn, Su Hua', 'Dawn Su Hua'], description: 'Su Hua & Dawn' },
  'casspri25': { expectedNames: ['Cassidy & Priscilla', 'Cassidy, Priscilla', 'Cassidy Priscilla', 'Priscilla & Cassidy', 'Priscilla, Cassidy', 'Priscilla Cassidy'], description: 'Cassidy & Priscilla' },
  'andwin25': { expectedNames: ['Andrew & Winnie', 'Andrew, Winnie', 'Andrew Winnie', 'Winnie & Andrew', 'Winnie, Andrew', 'Winnie Andrew'], description: 'Andrew & Winnie' },
  'chagat25': { expectedNames: ['Chagat'], description: 'Chagat' },
  'ramos25': { expectedNames: ['Ramos'], description: 'Ramos' },
  'vivi25': { expectedNames: ['Vivi'], description: 'Vivi' },
  'jane25': { expectedNames: ['Jane'], description: 'Jane' },
  'banmicmay25': { expectedNames: ['Bani, Michael, Mayline', 'Bani & Michael & Mayline', 'Bani Michael Mayline'], description: 'Bani, Michael, Mayline' },
  'roslyn25': { expectedNames: ['Roslyn'], description: 'Roslyn' },
  'chelaar25': { expectedNames: ['Chelsea & Aaron', 'Chelsea, Aaron', 'Chelsea Aaron', 'Aaron & Chelsea', 'Aaron, Chelsea', 'Aaron Chelsea'], description: 'Chelsea & Aaron' },
  'benj25': { expectedNames: ['Benjamin'], description: 'Benjamin' },
  'thiasel25': { expectedNames: ['Thiaga & Sellvi', 'Thiaga, Sellvi', 'Thiaga Sellvi', 'Sellvi & Thiaga', 'Sellvi, Thiaga', 'Sellvi Thiaga'], description: 'Thiaga & Sellvi' },
  'limjan25': { expectedNames: ['Lim SS & Janice', 'Lim SS, Janice', 'Lim SS Janice', 'Janice & Lim SS', 'Janice, Lim SS', 'Janice Lim SS'], description: 'Lim SS & Janice' },
  'ryumiz25': { expectedNames: ['Ryuta & Mizuho', 'Ryuta, Mizuho', 'Ryuta Mizuho', 'Mizuho & Ryuta', 'Mizuho, Ryuta', 'Mizuho Ryuta'], description: 'Ryuta & Mizuho' },
  'marctab25': { expectedNames: ['Marc & Tabitha', 'Marc, Tabitha', 'Marc Tabitha', 'Tabitha & Marc', 'Tabitha, Marc', 'Tabitha Marc'], description: 'Marc & Tabitha' },
  'izurei25': { expectedNames: ['Izumi & Reina', 'Izumi, Reina', 'Izumi Reina', 'Reina & Izumi', 'Reina, Izumi', 'Reina Izumi'], description: 'Izumi & Reina' },
  'jonstep25': { expectedNames: ['Jonathan & Stephanie', 'Jonathan, Stephanie', 'Jonathan Stephanie', 'Stephanie & Jonathan', 'Stephanie, Jonathan', 'Stephanie Jonathan'], description: 'Jonathan & Stephanie' },
  'denhae25': { expectedNames: ['Dennis & Haeji', 'Dennis, Haeji', 'Dennis Haeji', 'Haeji & Dennis', 'Haeji, Dennis', 'Haeji Dennis'], description: 'Dennis & Haeji' },
  'angmuj25': { expectedNames: ['Ang & Mujan', 'Ang, Mujan', 'Ang Mujan', 'Mujan & Ang', 'Mujan, Ang', 'Mujan Ang'], description: 'Ang & Mujan' },
  'desman25': { expectedNames: ['Desmond & Mana', 'Desmond, Mana', 'Desmond Mana', 'Mana & Desmond', 'Mana, Desmond', 'Mana Desmond'], description: 'Desmond & Mana' },
  'anndavdav25': { expectedNames: ['Anna, Davina, Awei, David', 'Anna & Davina & Awei & David', 'Anna Davina Awei David'], description: 'Anna, Davina, Awei, David' },
  'dickhel25': { expectedNames: ['Dickson, Helen, Azura, Azelina, Chester, Desmond', 'Dickson & Helen & Azura & Azelina & Chester & Desmond', 'Dickson Helen Azura Azelina Chester Desmond'], description: 'Dickson, Helen, Azura, Azelina, Chester, Desmond' },
  'yumryo25': { expectedNames: ['Yumi & Ryota', 'Yumi, Ryota', 'Yumi Ryota', 'Ryota & Yumi', 'Ryota, Yumi', 'Ryota Yumi'], description: 'Yumi & Ryota' },
  'kongjoa25': { expectedNames: ['Kong & Joanna', 'Kong, Joanna', 'Kong Joanna', 'Joanna & Kong', 'Joanna, Kong', 'Joanna Kong'], description: 'Kong & Joanna' },
  'shinmik25': { expectedNames: ['Shin, Mika, Ryuga, Emma', 'Shin & Mika & Ryuga & Emma', 'Shin Mika Ryuga Emma'], description: 'Shin, Mika, Ryuga, Emma' },
  'kohtam25': { expectedNames: ['Koharu, Tamao (Japan)', 'Koharu & Tamao'], description: 'Koharu, Tamao (Japan)' },
  'hyoin25': { expectedNames: ['Hyo In'], description: 'Hyo In' },
  'sheena25': { expectedNames: ['Sheena'], description: 'Sheena' },
  'stevlis25': { expectedNames: ['Steve & Lisa', 'Steve, Lisa', 'Steve Lisa', 'Lisa & Steve', 'Lisa, Steve', 'Lisa Steve', 'Steve & Lisa Muhs', 'Steve, Lisa Muhs', 'Steve Lisa Muhs', 'Lisa Muhs & Steve', 'Lisa Muhs, Steve', 'Lisa Muhs Steve'], description: 'Steve & Lisa' },
  'mikdeb25': { expectedNames: ['Mike & Debbie Jones', 'Mike, Debbie Jones', 'Mike Debbie Jones', 'Debbie Jones & Mike', 'Debbie Jones, Mike', 'Debbie Jones Mike', 'Mike & Debbie', 'Mike, Debbie', 'Mike Debbie', 'Debbie & Mike', 'Debbie, Mike', 'Debbie Mike'], description: 'Mike & Debbie Jones' },
  'chinang25': { expectedNames: ['Chin Kiet, Angeline', 'Chin Kiet & Angeline', 'Chin Kiet Angeline', 'Angeline & Chin Kiet', 'Angeline, Chin Kiet', 'Angeline Chin Kiet'], description: 'Chin Kiet, Angeline' },
  'johsue25': { expectedNames: ['John, Sue, Sonya', 'John & Sue & Sonya', 'John Sue Sonya'], description: 'John, Sue, Sonya' },
  'thanya25': { expectedNames: ['Thanya'], description: 'Thanya' },
  'dama25': { expectedNames: ['Dama'], description: 'Dama' },
  'lazarus25': { expectedNames: ['Lazarus'], description: 'Lazarus' },
  'shanlim25': { expectedNames: ['Shane Lim'], description: 'Shane Lim' },
  'simnancy25': { expectedNames: ['Simon & Nancy', 'Simon, Nancy', 'Simon Nancy', 'Nancy & Simon', 'Nancy, Simon', 'Nancy Simon'], description: 'Simon & Nancy' },
  'matlis25': { expectedNames: ['Matthew & Lisa', 'Matthew, Lisa', 'Matthew Lisa', 'Lisa & Matthew', 'Lisa, Matthew', 'Lisa Matthew'], description: 'Matthew & Lisa' },
  'shuena25': { expectedNames: ['Shuji & Ena', 'Shuji, Ena', 'Shuji Ena', 'Ena & Shuji', 'Ena, Shuji', 'Ena Shuji'], description: 'Shuji & Ena' },
  'bardar25': { expectedNames: ['Barinau & Dara', 'Barinau, Dara', 'Barinau Dara', 'Dara & Barinau', 'Dara, Barinau', 'Dara Barinau'], description: 'Barinau & Dara' },
  'patrick25': { expectedNames: ['Patrick'], description: 'Patrick' },
  'randy25': { expectedNames: ['Randy'], description: 'Randy' },
  'nancy25': { expectedNames: ['Nancy'], description: 'Nancy' },
  'yin25': { expectedNames: ['Yin'], description: 'Yin' },
  'hos25': { expectedNames: ['Hos'], description: 'Hos' },
  'anddol25': { expectedNames: ['Andrew & Dollin', 'Andrew, Dollin', 'Andrew Dollin', 'Dollin & Andrew', 'Dollin, Andrew', 'Dollin Andrew'], description: 'Andrew & Dollin' },
  'andmun25': { expectedNames: ['Andy, Munah', 'Andy & Munah', 'Andy Munah', 'Munah & Andy', 'Munah, Andy', 'Munah Andy'], description: 'Andy, Munah' },
  'kijseon25': { expectedNames: ['Ki Jeong & Seon Hwa', 'Ki Jeong, Seon Hwa', 'Ki Jeong Seon Hwa', 'Seon Hwa & Ki Jeong', 'Seon Hwa, Ki Jeong', 'Seon Hwa Ki Jeong'], description: 'Ki Jeong & Seon Hwa' },
  'koiyur25': { expectedNames: ['Koichi & Yuriya', 'Koichi, Yuriya', 'Koichi Yuriya', 'Yuriya & Koichi', 'Yuriya, Koichi', 'Yuriya Koichi'], description: 'Koichi & Yuriya' },
  'nanami25': { expectedNames: ['Nanami'], description: 'Nanami' },
  'kana25': { expectedNames: ['Kana'], description: 'Kana' },
  'inikung25': { expectedNames: ['Inik Unggon'], description: 'Inik Unggon' },
  'hapyii25': { expectedNames: ['Hap Yii'], description: 'Hap Yii' },
  'yianmei25': { expectedNames: ['Yian Mei'], description: 'Yian Mei' },
  'edwyn25': { expectedNames: ['Edwyn'], description: 'Edwyn' },
  // New Filler Groups
  'kadik25': { expectedNames: ['Kadik', 'Daisy', 'Heziel', 'Jeriel', 'Joanna', 'Kadik Daisy Heziel Jeriel Joanna', 'Kadik & Daisy & Heziel & Jeriel & Joanna', 'Kadik, Daisy, Heziel, Jeriel, Joanna'], description: 'Kadik, Daisy, Heziel, Jeriel, Joanna' },
  'pendi25': { expectedNames: ['Pendi', 'Alice', 'Harry', 'Jimmy', 'Princess', 'Pendi Alice Harry Jimmy Princess', 'Pendi & Alice & Harry & Jimmy & Princess', 'Pendi, Alice, Harry, Jimmy, Princess'], description: 'Pendi, Alice, Harry, Jimmy, Princess' },
  'yosua25': { expectedNames: ['Yosua', 'Roselind', 'Collina', 'Yosua Roselind Collina', 'Yosua & Roselind & Collina', 'Yosua, Roselind, Collina'], description: 'Yosua, Roselind, Collina' },
  'elah25': { expectedNames: ['Elah'], description: 'Elah' },
  'chiharu25': { expectedNames: ['Chiharu'], description: 'Chiharu' },
  'gawai25': { expectedNames: ['Gawai', 'Niok', 'Gawai Niok', 'Gawai & Niok', 'Gawai, Niok'], description: 'Gawai, Niok' },
  // Test codes
  'dominickemberly2025': { expectedNames: ['Test User', 'Test Guest', 'John Doe', 'Jane Doe'], description: 'Test wedding code for development purposes' }
};

function validateGuestNameForCode(weddingCode: string, guestName: string): boolean {
  const mapping = GUEST_WEDDING_CODE_MAPPING[weddingCode.toLowerCase()];
  if (!mapping) return true; // Allow if mapping not found

  const normalizedGuestName = guestName.toLowerCase().trim();
  return mapping.expectedNames.some(expectedName =>
    expectedName.toLowerCase() === normalizedGuestName
  );
}

// Helper function to determine if a wedding code represents a couple
function isCoupleCode(weddingCode: string): boolean {
  const mapping = GUEST_WEDDING_CODE_MAPPING[weddingCode.toLowerCase()];
  if (!mapping) return false;

  // Check if any expected name contains couple indicators (&, comma, or multiple names)
  return mapping.expectedNames.some(name =>
    name.includes('&') ||
    (name.includes(',') && !name.includes("'s")) || // Exclude possessive forms like "Dom's parents"
    (name.split(' ').length > 2 && !name.includes("'s")) // Multiple words but not possessive
  );
}

export const validateRSVPForm = (formData: RSVPFormData): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (!formData.guestName?.trim()) {
    errors.guestName = 'Please enter the names of all attending guests';
  } else if (formData.weddingCode && !validateGuestNameForCode(formData.weddingCode, formData.guestName)) {
    errors.guestName = 'Guest name doesn\'t match the expected names for this wedding code. Please check your name and try again.';
  }

  if (!formData.phone?.trim()) {
    errors.phone = 'Phone number is required';
  } else if (!/^\+?[\ds\-()]{10,}$/.test(formData.phone.replace(/\s/g, ''))) {
    errors.phone = 'Please enter a valid phone number';
  }

  // Only validate guest counts if attending
  if (formData.attending) {
    const totalGuests = (formData.adultCount || 0) + (formData.childCount || 0);

    if (totalGuests < 1) {
      errors.guestCount = 'Please select at least 1 guest';
    } else if (totalGuests > 6) {
      errors.guestCount = 'Maximum 6 guests allowed';
    }

    if (formData.adultCount < 1) {
      errors.adultCount = 'At least 1 adult is required';
    }

    if (formData.childCount < 0) {
      errors.childCount = 'Child count cannot be negative';
    }

    // Check for single attendance on couple codes
    if (totalGuests === 1 && formData.weddingCode && isCoupleCode(formData.weddingCode) && !formData.confirmSingleAttendance) {
      errors.singleAttendanceWarning = 'You are attending alone for a couple\'s wedding invitation. If one person from the couple cannot attend, please confirm this is correct.';
    }
  }

  if (!formData.weddingCode?.trim()) {
    errors.weddingCode = 'Wedding code is required';
  } else {
    if (!VALID_WEDDING_CODES.includes(formData.weddingCode.toLowerCase())) {
      errors.weddingCode = 'Invalid wedding code';
    }
  }

  return errors;
};

// Normalize phone number for comparison (remove spaces, dashes, parentheses, and handle country codes)
export const normalizePhoneNumber = (phone: string): string => {
  // Remove all non-digit characters except +
  let normalized = phone.replace(/[^\d+]/g, '');

  // Handle Malaysian country code (+60 or 60)
  if (normalized.startsWith('+60')) {
    normalized = normalized.substring(3); // Remove +60
  } else if (normalized.startsWith('60') && normalized.length > 10) {
    normalized = normalized.substring(2); // Remove 60
  }

  // Remove leading zero if present (Malaysian mobile numbers often start with 0)
  if (normalized.startsWith('0')) {
    normalized = normalized.substring(1);
  }

  return normalized;
};

// Async validation for duplicate phone numbers and wedding codes
export const validateRSVPFormAsync = async (formData: RSVPFormData): Promise<ValidationErrors> => {
  const { getRSVPResponses } = await import('./storage');
  
  // Run synchronous validation first
  const errors = validateRSVPForm(formData);
  if (Object.keys(errors).length > 0) {
    return errors;
  }
  
  // Get existing responses once for both checks
  let responses = [];
  try {
    responses = await getRSVPResponses();
  } catch (error) {
    // Silent error handling - failed to retrieve existing responses
    return errors;
  }

  // Check for duplicate phone number if phone is valid
  if (formData.phone?.trim() && !errors.phone) {
    const normalizedInputPhone = normalizePhoneNumber(formData.phone);

    // Check if any existing RSVP has the same normalized phone number
    const existingRSVP = responses.find(rsvp =>
      normalizePhoneNumber(rsvp.phone) === normalizedInputPhone
    );

    if (existingRSVP) {
      errors.phone = 'This phone number has already been used for an RSVP';
    }
  }

  // Check for duplicate wedding code if code is valid
  if (formData.weddingCode?.trim() && !errors.weddingCode) {
    const existingCodeRSVP = responses.find(rsvp =>
      rsvp.weddingCode.toLowerCase() === formData.weddingCode.toLowerCase()
    );

    if (existingCodeRSVP) {
      errors.weddingCode = 'This wedding code has already been used for an RSVP';
    }
  }

  return errors;
}

export const getInvitationType = (weddingCode: string): 'ceremony-only' | 'ceremony-reception' => {
  // All codes from your list are ceremony + reception
  return 'ceremony-reception';
};
